import { useTranslation } from "react-i18next";
import SEOHead from "../components/shared/SEOHead.jsx";
import JsonLd from "../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd } from "../utils/jsonld.js";
import SimplePage from "../components/shared/SimplePage.jsx";

const PRESS_PATH = { de: "presse", en: "press" };
const FACTSHEET_PATH = {
  de: "/assets/brand/documents/Factsheet_Tales_Legends.pdf",
  en: "/assets/brand/documents/Factsheet_TalesLegends_EN.pdf",
};
// Rendered from the same locale strings the release above is rendered
// from — see scripts/generate-press-release-pdf.js. Re-run that script
// after editing press.release*, or the download and the page drift apart.
const RELEASE_PDF_PATH = {
  de: "/assets/brand/documents/Pressemitteilung_TalesLegends_DE.pdf",
  en: "/assets/brand/documents/Pressemitteilung_TalesLegends_EN.pdf",
};

/** Logo pack and press photos live at tales.wtf; the fact sheet ships as a PDF per language. */
export default function Press({ lang }) {
  const { t } = useTranslation();

  return (
    <>
      <SEOHead
        lang={lang}
        path={`/${PRESS_PATH[lang]}`}
        altPath={`/${PRESS_PATH[lang === "de" ? "en" : "de"]}`}
        title={t("press.metaTitle")}
        description={t("press.metaDescription")}
      />
      <JsonLd
        data={buildBreadcrumbJsonLd(
          [
            { name: t("nav.home"), path: "/" },
            { name: t("press.title"), path: `/${PRESS_PATH[lang]}` },
          ],
          lang
        )}
      />
      <SimplePage title={t("press.title")}>
        <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.72)", margin: 0 }}>{t("press.intro")}</p>

        {/* A citable release, not marketing copy: every claim in it is one
          * the site already documents elsewhere (the events, the founding
          * details in the Impressum, the safeguarding concept). No quotes
          * — a quote has to come from the person it is attributed to. */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", borderLeft: "3px solid var(--color-gold)", paddingLeft: "22px" }}>
          <p style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "18px", color: "var(--color-ink)", margin: 0 }}>
            {t("press.releaseTitle")}
          </p>
          {/* 0.55 alpha over the cream card lands at 4.27:1 — under the
            * 4.5:1 axe enforces for text this size. */}
          <p style={{ fontSize: "13px", letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(10,9,7,0.68)", margin: 0 }}>
            {t("press.releaseDateline")}
          </p>
          <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "24px", lineHeight: 1.25, color: "var(--color-ink)", margin: 0 }}>
            {t("press.releaseHeadline")}
          </h2>
          {t("press.releaseBody", { returnObjects: true }).map((paragraph) => (
            <p key={paragraph.slice(0, 40)} style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 }}>
              {paragraph}
            </p>
          ))}
          <p style={{ fontSize: "14px", lineHeight: 1.65, color: "rgba(10,9,7,0.6)", margin: 0 }}>{t("press.releaseUsage")}</p>
          <a href={RELEASE_PDF_PATH[lang]} target="_blank" rel="noopener noreferrer" style={{ fontSize: "14px" }}>
            {t("press.releaseDownloadLink")}
          </a>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <p style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "18px", color: "var(--color-ink)", margin: 0 }}>
            {t("press.boilerplateTitle")}
          </p>
          <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 }}>{t("press.boilerplate")}</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <p style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "18px", color: "var(--color-ink)", margin: 0 }}>
            {t("press.factsTitle")}
          </p>
          <ul style={{ display: "flex", flexDirection: "column", gap: "6px", margin: 0, paddingLeft: "20px" }}>
            {["founded", "legalForm", "seat", "team", "events", "donations", "jugendschutz"].map((key) => (
              <li key={key} style={{ fontSize: "15px", lineHeight: 1.6, color: "rgba(10,9,7,0.8)" }}>
                {t(`press.facts.${key}`)}
              </li>
            ))}
          </ul>
        </div>

        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
          <li style={{ fontSize: "14px" }}>
            <a href="https://tales.wtf/logopack" target="_blank" rel="noopener noreferrer">
              {t("press.logoPackLink")}
            </a>
          </li>
          <li style={{ fontSize: "14px" }}>
            <a href={FACTSHEET_PATH[lang]} target="_blank" rel="noopener noreferrer">
              {t("press.factSheetLink")}
            </a>
          </li>
          <li style={{ fontSize: "14px" }}>
            <a href="https://tales.wtf/pressfoto" target="_blank" rel="noopener noreferrer">
              {t("press.photosLink")}
            </a>
          </li>
        </ul>

        <p style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "18px", color: "var(--color-ink)", margin: 0 }}>
          {t("press.contactTitle")}
        </p>
        <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.72)", margin: 0 }}>
          {lang === "de" ? (
            <>
              {"Bei Fragen wendet euch gerne an "}
              <a href="mailto:presse@talesandlegends.org">presse@talesandlegends.org</a>.
            </>
          ) : (
            <>
              {"For questions, feel free to reach out to "}
              <a href="mailto:presse@talesandlegends.org">presse@talesandlegends.org</a>.
            </>
          )}
        </p>
      </SimplePage>
    </>
  );
}

export { PRESS_PATH };
